import { Fragment } from 'react';
import { trans } from '@spotahome/soyuz/client';
import PropTypes from 'prop-types';

import ListingTitle from '../ListingTitle';
import { ListingSectionObservable } from '../ListingSection';
import ListingChipsContainer from '../ListingChipsContainer';

import {
  formatScheduleRanges,
  formatFeaturesToChipList,
  formatDocumentsToChipList
} from './utils';

import './PropertyRulesRedesign.scss';

const PropertyRulesRedesignMoveInMoveOutSection = ({
  dateSections = [],
  tip = null
}) => {
  return (
    <div className="property-rules-redesign-move-in-out__item">
      {dateSections
        .filter(
          dateSection => dateSection.content && dateSection.content.length > 0
        )
        .map(dateSection => (
          <div
            className="property-rules-redesign-move-in-out__item-section"
            key={dateSection.title}
          >
            <span className="property-rules-redesign-move-in-out__item-title">
              {dateSection.title}
            </span>
            <span className="property-rules-redesign-move-in-out__item-description">
              {dateSection.content}
            </span>
          </div>
        ))}
      {!!tip && (
        <span className="property-rules-redesign-move-in-out__item-tip">{tip}</span>
      )}
    </div>
  );
};

PropertyRulesRedesignMoveInMoveOutSection.propTypes = {
  dateSections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.arrayOf(PropTypes.node)
      ])
    })
  ),
  tip: PropTypes.string
};

const PropertyRulesRedesign = ({
  propertyRules,
  documentsPolicy,
  moveInMoveOutPolicy,
  contractualPermissions,
  currency
}) => {
  const { moveIn, moveOut, tip } = formatScheduleRanges(
    moveInMoveOutPolicy.moveInMoveOutSchedules?.ranges
  );
  const propertyFeaturesChips = formatFeaturesToChipList(
    propertyRules,
    currency
  ).filter(Boolean);

  const contractualPermissionsChips = formatFeaturesToChipList(
    contractualPermissions
  ).filter(Boolean);

  const documentsPolicyChips =
    formatDocumentsToChipList(documentsPolicy).filter(Boolean);

  const hasCheckInCheckOut = !!moveIn.length || !!moveOut.length;
  const hasMinimumNoticePeriod =
    !!moveInMoveOutPolicy.minimumNoticePeriod?.days;

  return (
    <ListingSectionObservable section="landlordRules">
      <ListingTitle role="secondary">
        {trans('property.rule.main.title')}
      </ListingTitle>
      <div className="property-rules-redesign__section">
        <ListingTitle role="tertiary">
          {trans('property.rule.propertyRules.title')}
        </ListingTitle>
        <ListingChipsContainer chipList={propertyFeaturesChips} />
      </div>
      {!!contractualPermissionsChips.length && (
        <div className="property-rules-redesign__section">
          <ListingTitle role="tertiary">
            {trans('property.rule.contractualPermissions.title')}
          </ListingTitle>
          <ListingChipsContainer chipList={contractualPermissionsChips} />
        </div>
      )}
      {!!documentsPolicyChips.length && (
        <div className="property-rules-redesign__section">
          <ListingTitle role="tertiary">
            {trans('property.rule.documentsPolicy.title')}
          </ListingTitle>
          <p>{trans('property.rule.documentsPolicy.tip')}</p>
          <ListingChipsContainer
            chipList={documentsPolicyChips}
            className="property-rules-redesign__documents-policy-chips"
          />
          {documentsPolicy.requiredDocumentation?.additionalInfo ? (
            <p>
              <strong>
                {trans('property.rule.documentsPolicy.otherDocuments')}
              </strong>{' '}
              {documentsPolicy.requiredDocumentation.additionalInfo}
            </p>
          ) : null}
        </div>
      )}
      {(hasCheckInCheckOut || hasMinimumNoticePeriod) && (
        <div className="property-rules-redesign__section">
          <ListingTitle role="tertiary">
            {trans('property.rule.moveInMoveOut.title')}
          </ListingTitle>
          <div className="property-rules-redesign-move-in-out">
            <PropertyRulesRedesignMoveInMoveOutSection
              dateSections={[
                {
                  title: trans('property.rule.moveInMoveOut.checkIn'),
                  content: moveIn.map(range => <span key={range}>{range}</span>)
                },
                {
                  title: trans('property.rule.moveInMoveOut.checkOut'),
                  content: moveOut.map(range => (
                    <span key={range}>{range}</span>
                  ))
                }
              ]}
              tip={
                moveInMoveOutPolicy.moveInMoveOutSchedules?.price ? tip : null
              }
            />
            {hasMinimumNoticePeriod && (
              <PropertyRulesRedesignMoveInMoveOutSection
                dateSections={[
                  {
                    title: trans(
                      'property.rule.moveInMoveOut.earlyDepartures.title'
                    ),
                    content: trans(
                      'property.rule.moveInMoveOut.earlyDepartures.description',
                      { nDays: moveInMoveOutPolicy.minimumNoticePeriod.days }
                    )
                  }
                ]}
                tip={
                  moveInMoveOutPolicy.minimumNoticePeriod?.price
                    ? trans('property.rule.moveInMoveOut.earlyDepartures.tip')
                    : null
                }
              />
            )}
          </div>
        </div>
      )}
    </ListingSectionObservable>
  );
};

PropertyRulesRedesign.propTypes = {
  propertyRules: PropTypes.shape({
    landlordLivesInHouse: PropTypes.shape({
      icon: PropTypes.string,
      locoKey: PropTypes.string,
      landlordBirthday: PropTypes.string,
      landlordGender: PropTypes.string
    }),
    smokingAllowed: PropTypes.shape({
      icon: PropTypes.string,
      locoKey: PropTypes.string,
      additionalInfo: PropTypes.string
    }),
    petsAllowed: PropTypes.shape({
      icon: PropTypes.string,
      locoKey: PropTypes.string,
      allowedTypes: PropTypes.string,
      additionalInfo: PropTypes.string,
      price: PropTypes.number
    }),
    overnightGuests: PropTypes.shape({
      icon: PropTypes.string,
      locoKey: PropTypes.string,
      additionalInfo: PropTypes.string,
      price: PropTypes.number
    }),
    couplesAllowed: PropTypes.shape({
      icon: PropTypes.string,
      locoKey: PropTypes.string,
      additionalInfo: PropTypes.string
    }),
    minimumAndMaximumAge: PropTypes.shape({
      icon: PropTypes.string,
      locoKey: PropTypes.string,
      min: PropTypes.number,
      max: PropTypes.number
    })
  }).isRequired,
  documentsPolicy: PropTypes.shape({
    requiredDocumentation: PropTypes.shape({
      documents: PropTypes.arrayOf(PropTypes.string),
      additionalInfo: PropTypes.string
    })
  }).isRequired,
  moveInMoveOutPolicy: PropTypes.shape({
    minimumNoticePeriod: PropTypes.shape({
      days: PropTypes.number,
      price: PropTypes.number
    }),
    moveInMoveOutSchedules: PropTypes.shape({
      price: PropTypes.number,
      ranges: PropTypes.arrayOf(
        PropTypes.shape({
          days: PropTypes.arrayOf(PropTypes.string),
          checkIn: PropTypes.shape({
            from: PropTypes.string,
            to: PropTypes.string
          }),
          checkOut: PropTypes.shape({
            from: PropTypes.string,
            to: PropTypes.string
          })
        })
      )
    })
  }).isRequired,
  contractualPermissions: PropTypes.shape({
    domiciliationAllowed: PropTypes.shape({
      icon: PropTypes.string,
      locoKey: PropTypes.string,
      additionalInfo: PropTypes.string
    }),
    newContractValidForScholarship: PropTypes.shape({
      icon: PropTypes.string,
      locoKey: PropTypes.string,
      additionalInfo: PropTypes.string
    })
  }).isRequired,
  currency: PropTypes.string.isRequired
};

export default PropertyRulesRedesign;
